import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Plus, Search, Users, Phone, MapPin, Calendar, Edit, Eye, 
  RefreshCw, Loader2, TrendingUp, TrendingDown, Wallet, CreditCard,
  ArrowUpDown, ArrowUp, ArrowDown, FileText, Receipt, X, Building2, Mail, CreditCard as CardIcon, User,
  Download
} from 'lucide-react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import DocumentPreviewModal from '../../components/DocumentPreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AccountingCariHesaplar = () => {
  const [hesaplar, setHesaplar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedHesap, setSelectedHesap] = useState(null);
  const [formData, setFormData] = useState({
    ad: '',
    vergi_no: '',
    yetkili: '',
    adres: '',
    il: '',
    telefon: '',
    email: '',
    iban: '',
    hesap_sahibi: ''
  });
  
  // Detay modalı state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailHesap, setDetailHesap] = useState(null);
  
  // Belgeler modal state
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewBelgeler, setPreviewBelgeler] = useState([]);
  const [previewTitle, setPreviewTitle] = useState('Belgeler');
  
  // Çekler state
  const [cekler, setCekler] = useState([]);
  const [showCekModal, setShowCekModal] = useState(false);
  const [selectedCekType, setSelectedCekType] = useState('alinan');
  const [selectedCariForCek, setSelectedCariForCek] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  useEffect(() => {
    loadHesaplar();
  }, []);

  const loadHesaplar = async () => {
    setLoading(true);
    try {
      const [hesapRes, cekRes] = await Promise.all([
        axios.get(`${API}/accounting/cari-hesaplar`, { headers: getAuthHeaders() }),
        axios.get(`${API}/accounting/cekler`, { headers: getAuthHeaders() })
      ]);
      setHesaplar(hesapRes.data || []);
      setCekler(cekRes.data || []);
    } catch (error) {
      console.error('Cari hesaplar yüklenirken hata:', error);
      toast.error('Cari hesaplar yüklenemedi');
    }
    setLoading(false);
  };

  // Cari hesaba ait çekleri hesapla (tip: "alinan" veya "verilen")
  const getCekCount = (cariAd, type) => {
    return cekler.filter(c => 
      c.cari_hesap_adi?.toLowerCase() === cariAd?.toLowerCase() && 
      c.tip === type
    ).length;
  };

  const getCeklerForCari = (cariAd, type) => {
    return cekler.filter(c => 
      c.cari_hesap_adi?.toLowerCase() === cariAd?.toLowerCase() && 
      c.tip === type
    );
  };

  const openCekModal = (hesap, type) => {
    setSelectedCariForCek(hesap);
    setSelectedCekType(type);
    setShowCekModal(true);
  };

  const handleCreate = async () => {
    if (!formData.ad.trim()) {
      toast.error('Cari hesap adı gerekli');
      return;
    }
    try {
      // Global cari hesap olarak kaydet (Satınalma ile aynı)
      const globalData = {
        name: formData.ad,
        city: formData.il,
        phone: formData.telefon,
        vergi_no: formData.vergi_no,
        yetkili: formData.yetkili,
        adres: formData.adres,
        email: formData.email,
        iban: formData.iban,
        hesap_sahibi: formData.hesap_sahibi
      };
      const res = await axios.post(`${API}/global/cari-hesaplar`, globalData, { headers: getAuthHeaders() });
      if (res.status === 200 || res.status === 201) {
        toast.success('Cari hesap eklendi');
        setModalOpen(false);
        setFormData({ ad: '', vergi_no: '', yetkili: '', adres: '', il: '', telefon: '', email: '', iban: '', hesap_sahibi: '' });
        loadHesaplar();
      } else {
        toast.error('Cari hesap eklenemedi');
      }
    } catch (error) {
      console.error('Cari hesap ekleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Cari hesap eklenemedi');
    }
  };

  const handleUpdate = async () => {
    if (!formData.ad.trim()) {
      toast.error('Cari hesap adı gerekli');
      return;
    }
    try {
      const res = await axios.put(`${API}/accounting/cari-hesaplar/${selectedHesap.id}`, formData, { headers: getAuthHeaders() });
      if (res.status === 200 || res.status === 201) {
        toast.success('Cari hesap güncellendi');
        setEditModalOpen(false);
        setSelectedHesap(null);
        setFormData({ ad: '', vergi_no: '', yetkili: '', adres: '', il: '', telefon: '', email: '', iban: '', hesap_sahibi: '' });
        loadHesaplar();
      } else {
        toast.error('Cari hesap güncellenemedi');
      }
    } catch (error) {
      console.error('Cari hesap güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Cari hesap güncellenemedi');
    }
  };

  // Detay modalını aç
  const openDetailModal = (hesap) => {
    setDetailHesap(hesap);
    setDetailModalOpen(true);
  };

  // Silme fonksiyonu kaldırıldı - sadece düzenleme ve görüntüleme mümkün

  const openEditModal = (hesap) => {
    setSelectedHesap(hesap);
    setFormData({
      ad: hesap.ad || hesap.name || '',
      vergi_no: hesap.vergi_no || '',
      yetkili: hesap.yetkili || '',
      adres: hesap.adres || '',
      il: hesap.il || hesap.city || '',
      telefon: hesap.telefon || hesap.phone || '',
      email: hesap.email || '',
      iban: hesap.iban || '',
      hesap_sahibi: hesap.hesap_sahibi || ''
    });
    setEditModalOpen(true);
  };

  const filteredHesaplar = hesaplar.filter(h => 
    h.ad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.il?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.telefon?.includes(searchTerm)
  );

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-indigo-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-indigo-600" />;
  };

  // Sorted data
  const sortedHesaplar = useMemo(() => {
    const sorted = [...filteredHesaplar];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined
      if (aVal == null) aVal = sortConfig.key.includes('toplam') || sortConfig.key.includes('kalan') || sortConfig.key.includes('fatura') || sortConfig.key.includes('borc') ? 0 : '';
      if (bVal == null) bVal = sortConfig.key.includes('toplam') || sortConfig.key.includes('kalan') || sortConfig.key.includes('fatura') || sortConfig.key.includes('borc') ? 0 : '';
      
      // Numeric comparison for financial columns
      if (['toplam_gelen', 'toplam_odenen', 'kalan_alacak', 'kalan_odeme', 'giris_fatura_kesilen', 'giris_fatura_kesilen_kdv', 'giris_borc_alinan', 'cikis_fatura_alinan', 'cikis_fatura_alinan_kdv', 'cikis_borc_verilen', 'alinmasi_gereken_fatura', 'alinmasi_gereken_kdv', 'kesilmesi_gereken_fatura', 'kesilmesi_gereken_kdv', 'belge_sayisi'].includes(sortConfig.key)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      // Date comparison
      if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // String comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredHesaplar, sortConfig]);

  // Excel indirme fonksiyonu
  const handleExportExcel = () => {
    try {
      // Tablo verilerini Excel formatına çevir
      const excelData = sortedHesaplar.map(hesap => ({
        'Cari Hesap Adı': hesap.ad || hesap.name || '',
        'Şehir': hesap.il || hesap.city || '',
        'Telefon': hesap.telefon || hesap.phone || '',
        'Toplam Gelen': hesap.toplam_gelen || 0,
        'Toplam Ödenen': hesap.toplam_odenen || 0,
        'Kalan Alacak': hesap.kalan_alacak || 0,
        'Kalan Ödeme': hesap.kalan_odeme || 0,
        'Kesilen Fatura': hesap.giris_fatura_kesilen || 0,
        'Ödenen KDV': hesap.giris_fatura_kesilen_kdv || 0,
        'Alınan Fatura': hesap.cikis_fatura_alinan || 0,
        'Alınan KDV': hesap.cikis_fatura_alinan_kdv || 0,
        'Kesilmesi Gereken': hesap.kesilmesi_gereken_fatura || 0,
        'Kesilmesi Gereken KDV': hesap.kesilmesi_gereken_kdv || 0,
        'Alınması Gereken': hesap.alinmasi_gereken_fatura || 0,
        'Alınması Gereken KDV': hesap.alinmasi_gereken_kdv || 0,
        'Belge Sayısı': hesap.belgeler?.length || 0,
        'Alınan Çek': hesap.alinan_cekler?.length || 0,
        'Vergi No': hesap.vergi_no || '',
        'Yetkili': hesap.yetkili || '',
        'Adres': hesap.adres || '',
        'E-mail': hesap.email || '',
        'IBAN': hesap.iban || '',
        'Oluşturma Tarihi': hesap.created_at ? new Date(hesap.created_at).toLocaleDateString('tr-TR') : ''
      }));

      // Excel dosyası oluştur
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cari Hesaplar');

      // Sütun genişliklerini ayarla
      const colWidths = [
        { wch: 25 }, // Cari Hesap Adı
        { wch: 12 }, // Şehir
        { wch: 15 }, // Telefon
        { wch: 15 }, // Toplam Gelen
        { wch: 15 }, // Toplam Ödenen
        { wch: 15 }, // Kalan Alacak
        { wch: 15 }, // Kalan Ödeme
        { wch: 15 }, // Kesilen Fatura
        { wch: 12 }, // Ödenen KDV
        { wch: 15 }, // Alınan Fatura
        { wch: 12 }, // Alınan KDV
        { wch: 18 }, // Kesilmesi Gereken
        { wch: 18 }, // Alınması Gereken
        { wch: 12 }, // Belge Sayısı
        { wch: 12 }, // Alınan Çek
        { wch: 15 }, // Vergi No
        { wch: 20 }, // Yetkili
        { wch: 30 }, // Adres
        { wch: 25 }, // E-mail
        { wch: 30 }, // IBAN
        { wch: 15 }, // Oluşturma Tarihi
      ];
      ws['!cols'] = colWidths;

      // Dosyayı indir
      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Cari_Hesaplar_${today}.xlsx`);
      
      toast.success(`${sortedHesaplar.length} cari hesap Excel olarak indirildi`);
    } catch (error) {
      console.error('Excel indirme hatası:', error);
      toast.error('Excel indirme sırasında hata oluştu');
    }
  };

  return (
    <div className="space-y-6" data-testid="cari-hesaplar-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CARİ HESAPLAR</h1>
          <p className="text-gray-500">Cari hesap kartlarını yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleExportExcel} 
            variant="outline" 
            size="sm" 
            className="gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            Excel İndir
          </Button>
          <Button onClick={loadHesaplar} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button 
            onClick={() => { setFormData({ ad: '', vergi_no: '', yetkili: '', adres: '', il: '', telefon: '', email: '', iban: '', hesap_sahibi: '' }); setModalOpen(true); }}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-cari-hesap-btn"
          >
            <Plus className="h-4 w-4" />
            Cari Hesap Ekle
          </Button>
        </div>
      </div>

      {/* İstatistik Kartı */}
      <Card className="bg-indigo-50 border-indigo-200">
        <CardContent className="p-4 flex items-center gap-3">
          <Users className="h-8 w-8 text-indigo-600" />
          <div>
            <p className="text-2xl font-bold text-indigo-700">{hesaplar.length}</p>
            <p className="text-sm text-indigo-600">Toplam Cari Hesap</p>
          </div>
        </CardContent>
      </Card>

      {/* Arama */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Cari hesap ara (ad, il, telefon)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-cari-hesap"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tablo */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredHesaplar.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Cari Hesap Bulunmuyor</h3>
            <p className="text-gray-500">Henüz cari hesap eklenmemiş</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[1400px]">
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Oluşturma Tarihi
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('ad')}
                  >
                    <div className="flex items-center">
                      Cari Hesap Adı
                      {getSortIcon('ad')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('il')}
                  >
                    <div className="flex items-center">
                      İl
                      {getSortIcon('il')}
                    </div>
                  </TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('toplam_gelen')}
                  >
                    <div className="flex items-center justify-end">
                      Toplam Gelen
                      {getSortIcon('toplam_gelen')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('toplam_odenen')}
                  >
                    <div className="flex items-center justify-end">
                      Toplam Ödenen
                      {getSortIcon('toplam_odenen')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('kalan_alacak')}
                  >
                    <div className="flex items-center justify-end">
                      Kalan Alacak
                      {getSortIcon('kalan_alacak')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('kalan_odeme')}
                  >
                    <div className="flex items-center justify-end">
                      Kalan Ödeme
                      {getSortIcon('kalan_odeme')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('giris_fatura_kesilen')}
                  >
                    <div className="flex items-center justify-end">
                      Kesilen Fatura
                      {getSortIcon('giris_fatura_kesilen')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none bg-emerald-50"
                    onClick={() => handleSort('giris_fatura_kesilen_kdv')}
                  >
                    <div className="flex items-center justify-end">
                      Ödenen KDV
                      {getSortIcon('giris_fatura_kesilen_kdv')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('cikis_fatura_alinan')}
                  >
                    <div className="flex items-center justify-end">
                      Alınan Fatura
                      {getSortIcon('cikis_fatura_alinan')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none bg-orange-50"
                    onClick={() => handleSort('cikis_fatura_alinan_kdv')}
                  >
                    <div className="flex items-center justify-end">
                      Alınan KDV
                      {getSortIcon('cikis_fatura_alinan_kdv')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none bg-blue-50"
                    onClick={() => handleSort('kesilmesi_gereken_fatura')}
                  >
                    <div className="flex items-center justify-end">
                      Kesilmesi Gereken
                      {getSortIcon('kesilmesi_gereken_fatura')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none bg-cyan-50"
                    onClick={() => handleSort('kesilmesi_gereken_kdv')}
                  >
                    <div className="flex items-center justify-end">
                      Kesilmesi Gereken KDV
                      {getSortIcon('kesilmesi_gereken_kdv')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none bg-orange-50"
                    onClick={() => handleSort('alinmasi_gereken_fatura')}
                  >
                    <div className="flex items-center justify-end">
                      Alınması Gereken
                      {getSortIcon('alinmasi_gereken_fatura')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-gray-50 select-none bg-purple-50"
                    onClick={() => handleSort('alinmasi_gereken_kdv')}
                  >
                    <div className="flex items-center justify-end">
                      Alınması Gereken KDV
                      {getSortIcon('alinmasi_gereken_kdv')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('belge_sayisi')}
                  >
                    <div className="flex items-center">
                      Belgeler
                      {getSortIcon('belge_sayisi')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-gray-50 select-none"
                  >
                    <div className="flex items-center justify-center">
                      Alınan Çek
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-gray-50 select-none"
                  >
                    <div className="flex items-center justify-center">
                      Verilen Çek
                    </div>
                  </TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedHesaplar.map(hesap => (
                  <TableRow key={hesap.id} data-testid={`cari-row-${hesap.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        {new Date(hesap.created_at).toLocaleDateString('tr-TR')}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{hesap.ad}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {hesap.il || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        {hesap.telefon || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${hesap.toplam_gelen > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {formatCurrency(hesap.toplam_gelen)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${hesap.toplam_odenen > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {formatCurrency(hesap.toplam_odenen)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {hesap.kalan_alacak > 0 ? (
                        <Badge className="bg-emerald-100 text-emerald-700 font-medium">
                          {formatCurrency(hesap.kalan_alacak)}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {hesap.kalan_odeme > 0 ? (
                        <Badge className="bg-red-100 text-red-700 font-medium">
                          {formatCurrency(hesap.kalan_odeme)}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {hesap.giris_fatura_kesilen > 0 ? (
                        <span className="font-medium text-emerald-600">
                          {formatCurrency(hesap.giris_fatura_kesilen)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right bg-emerald-50/50">
                      {hesap.giris_fatura_kesilen_kdv > 0 ? (
                        <span className="font-medium text-emerald-700">
                          {formatCurrency(hesap.giris_fatura_kesilen_kdv)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {hesap.cikis_fatura_alinan > 0 ? (
                        <span className="font-medium text-orange-600">
                          {formatCurrency(hesap.cikis_fatura_alinan)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right bg-orange-50/50">
                      {hesap.cikis_fatura_alinan_kdv > 0 ? (
                        <span className="font-medium text-orange-700">
                          {formatCurrency(hesap.cikis_fatura_alinan_kdv)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    {/* Kesilmesi Gereken Fatura (Girişlerden - Biz keseceğiz) */}
                    <TableCell className="text-right bg-blue-50/50">
                      {hesap.kesilmesi_gereken_fatura > 0 ? (
                        <span className="font-medium text-blue-700">
                          {formatCurrency(hesap.kesilmesi_gereken_fatura)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    {/* Kesilmesi Gereken KDV */}
                    <TableCell className="text-right bg-cyan-50/50">
                      {hesap.kesilmesi_gereken_kdv > 0 ? (
                        <span className="font-medium text-cyan-700">
                          {formatCurrency(hesap.kesilmesi_gereken_kdv)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    {/* Alınması Gereken Fatura (Çıkışlardan - Bize gelecek) */}
                    <TableCell className="text-right bg-orange-50/50">
                      {hesap.alinmasi_gereken_fatura > 0 ? (
                        <span className="font-medium text-orange-700">
                          {formatCurrency(hesap.alinmasi_gereken_fatura)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    {/* Alınması Gereken KDV */}
                    <TableCell className="text-right bg-purple-50/50">
                      {hesap.alinmasi_gereken_kdv > 0 ? (
                        <span className="font-medium text-purple-700">
                          {formatCurrency(hesap.alinmasi_gereken_kdv)}
                        </span>
                      ) : (
                        <span className="text-gray-400">{formatCurrency(0)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hesap.belgeler && hesap.belgeler.length > 0 ? (
                        <button
                          onClick={() => {
                            setPreviewBelgeler(hesap.belgeler);
                            setPreviewTitle(`${hesap.ad} - Belgeler`);
                            setShowDocPreview(true);
                          }}
                          className="flex items-center gap-1 cursor-pointer hover:underline"
                          data-testid={`cari-belgeler-${hesap.id}`}
                        >
                          <FileText className="h-4 w-4 text-blue-600" />
                          <span className="text-xs text-blue-600">{hesap.belgeler.length} belge</span>
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </TableCell>
                    {/* Alınan Çek Sütunu */}
                    <TableCell className="text-center">
                      {getCekCount(hesap.ad, 'alinan') > 0 ? (
                        <button
                          onClick={() => openCekModal(hesap, 'alinan')}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors cursor-pointer"
                          data-testid={`cari-alinan-cek-${hesap.id}`}
                        >
                          <Receipt className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm font-medium text-emerald-700">{getCekCount(hesap.ad, 'alinan')}</span>
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    {/* Verilen Çek Sütunu */}
                    <TableCell className="text-center">
                      {getCekCount(hesap.ad, 'verilen') > 0 ? (
                        <button
                          onClick={() => openCekModal(hesap, 'verilen')}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors cursor-pointer"
                          data-testid={`cari-verilen-cek-${hesap.id}`}
                        >
                          <Receipt className="h-4 w-4 text-orange-600" />
                          <span className="text-sm font-medium text-orange-700">{getCekCount(hesap.ad, 'verilen')}</span>
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openDetailModal(hesap)}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`view-cari-${hesap.id}`}
                          title="Detayları Görüntüle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditModal(hesap)}
                          data-testid={`edit-cari-${hesap.id}`}
                          title="Düzenle"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ekleme Modalı */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-indigo-600" />
              Yeni Cari Hesap Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <Label className="text-gray-500 text-sm">Oluşturma Tarihi</Label>
              <p className="font-medium">{new Date().toLocaleDateString('tr-TR')} {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="ad">Cari Hesap Adı *</Label>
                <Input 
                  id="ad"
                  value={formData.ad}
                  onChange={(e) => setFormData({ ...formData, ad: e.target.value })}
                  placeholder="Firma adı"
                  data-testid="cari-ad-input"
                />
              </div>
              
              <div>
                <Label htmlFor="vergi_no">Vergi No</Label>
                <Input 
                  id="vergi_no"
                  value={formData.vergi_no}
                  onChange={(e) => setFormData({ ...formData, vergi_no: e.target.value })}
                  placeholder="Vergi numarası"
                  data-testid="cari-vergi-input"
                />
              </div>
              
              <div>
                <Label htmlFor="yetkili">Yetkili</Label>
                <Input 
                  id="yetkili"
                  value={formData.yetkili}
                  onChange={(e) => setFormData({ ...formData, yetkili: e.target.value })}
                  placeholder="Yetkili kişi adı"
                  data-testid="cari-yetkili-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="adres">Adres</Label>
                <Input 
                  id="adres"
                  value={formData.adres}
                  onChange={(e) => setFormData({ ...formData, adres: e.target.value })}
                  placeholder="Firma adresi"
                  data-testid="cari-adres-input"
                />
              </div>
              
              <div>
                <Label htmlFor="il">Şehir</Label>
                <Input 
                  id="il"
                  value={formData.il}
                  onChange={(e) => setFormData({ ...formData, il: e.target.value })}
                  placeholder="Şehir"
                  data-testid="cari-il-input"
                />
              </div>
              
              <div>
                <Label htmlFor="telefon">Telefon</Label>
                <Input 
                  id="telefon"
                  value={formData.telefon}
                  onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                  placeholder="Telefon numarası"
                  data-testid="cari-telefon-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input 
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="E-posta adresi"
                  data-testid="cari-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="iban">IBAN</Label>
                <Input 
                  id="iban"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  data-testid="cari-iban-input"
                />
              </div>
              
              <div>
                <Label htmlFor="hesap_sahibi">Hesap Sahibi</Label>
                <Input 
                  id="hesap_sahibi"
                  value={formData.hesap_sahibi}
                  onChange={(e) => setFormData({ ...formData, hesap_sahibi: e.target.value })}
                  placeholder="Hesap sahibi adı"
                  data-testid="cari-hesap-sahibi-input"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-cari-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzenleme Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-indigo-600" />
              Cari Hesap Düzenle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <Label className="text-gray-500 text-sm">Oluşturma Tarihi</Label>
              <p className="font-medium">
                {selectedHesap ? new Date(selectedHesap.created_at).toLocaleDateString('tr-TR') : '-'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="edit-ad">Cari Hesap Adı *</Label>
                <Input 
                  id="edit-ad"
                  value={formData.ad}
                  onChange={(e) => setFormData({ ...formData, ad: e.target.value })}
                  placeholder="Firma adı"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-vergi_no">Vergi No</Label>
                <Input 
                  id="edit-vergi_no"
                  value={formData.vergi_no}
                  onChange={(e) => setFormData({ ...formData, vergi_no: e.target.value })}
                  placeholder="Vergi numarası"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-yetkili">Yetkili</Label>
                <Input 
                  id="edit-yetkili"
                  value={formData.yetkili}
                  onChange={(e) => setFormData({ ...formData, yetkili: e.target.value })}
                  placeholder="Yetkili kişi adı"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="edit-adres">Adres</Label>
                <Input 
                  id="edit-adres"
                  value={formData.adres}
                  onChange={(e) => setFormData({ ...formData, adres: e.target.value })}
                  placeholder="Firma adresi"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-il">Şehir</Label>
                <Input 
                  id="edit-il"
                  value={formData.il}
                  onChange={(e) => setFormData({ ...formData, il: e.target.value })}
                  placeholder="Şehir"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-telefon">Telefon</Label>
                <Input 
                  id="edit-telefon"
                  value={formData.telefon}
                  onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                  placeholder="Telefon numarası"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-email">E-mail</Label>
                <Input 
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="E-posta adresi"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-iban">IBAN</Label>
                <Input 
                  id="edit-iban"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-hesap_sahibi">Hesap Sahibi</Label>
                <Input 
                  id="edit-hesap_sahibi"
                  value={formData.hesap_sahibi}
                  onChange={(e) => setFormData({ ...formData, hesap_sahibi: e.target.value })}
                  placeholder="Hesap sahibi adı"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdate}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={showDocPreview}
        onOpenChange={setShowDocPreview}
        belgeler={previewBelgeler}
        title={previewTitle}
      />

      {/* Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Cari Hesap Detayı
            </DialogTitle>
          </DialogHeader>
          
          {detailHesap && (
            <div className="space-y-6">
              {/* Firma Bilgileri */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-100">
                <h3 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Firma Bilgileri
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500 text-xs">Cari Hesap Adı</Label>
                    <p className="font-medium text-gray-900">{detailHesap.ad || detailHesap.name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Vergi No</Label>
                    <p className="font-medium text-gray-900">{detailHesap.vergi_no || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Yetkili</Label>
                    <p className="font-medium text-gray-900">{detailHesap.yetkili || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Oluşturma Tarihi</Label>
                    <p className="font-medium text-gray-900">
                      {detailHesap.created_at ? new Date(detailHesap.created_at).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* İletişim Bilgileri */}
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg border border-emerald-100">
                <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  İletişim Bilgileri
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-gray-500 text-xs">Adres</Label>
                    <p className="font-medium text-gray-900">{detailHesap.adres || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Şehir</Label>
                    <p className="font-medium text-gray-900">{detailHesap.il || detailHesap.city || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Telefon</Label>
                    <p className="font-medium text-gray-900">{detailHesap.telefon || detailHesap.phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">E-mail</Label>
                    <p className="font-medium text-gray-900">{detailHesap.email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Banka Bilgileri */}
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-100">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <CardIcon className="h-5 w-5" />
                  Banka Bilgileri
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-gray-500 text-xs">IBAN</Label>
                    <p className="font-medium text-gray-900 font-mono">{detailHesap.iban || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Hesap Sahibi</Label>
                    <p className="font-medium text-gray-900">{detailHesap.hesap_sahibi || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Finansal Özet */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100">
                <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  Finansal Özet
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                    <Label className="text-gray-500 text-xs">Toplam Gelen</Label>
                    <p className="font-bold text-emerald-600">{formatCurrency(detailHesap.toplam_gelen)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                    <Label className="text-gray-500 text-xs">Toplam Ödenen</Label>
                    <p className="font-bold text-orange-600">{formatCurrency(detailHesap.toplam_odenen)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                    <Label className="text-gray-500 text-xs">Kalan Alacak</Label>
                    <p className="font-bold text-teal-600">{formatCurrency(detailHesap.kalan_alacak)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                    <Label className="text-gray-500 text-xs">Kalan Ödeme</Label>
                    <p className="font-bold text-red-600">{formatCurrency(detailHesap.kalan_odeme)}</p>
                  </div>
                </div>
                
                {/* Fatura Bilgileri */}
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <h4 className="text-sm font-medium text-purple-700 mb-2 flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    Fatura Durumu
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center p-2 bg-white rounded-lg shadow-sm border-l-4 border-emerald-500">
                      <Label className="text-gray-500 text-xs">Kesilen Fatura</Label>
                      <p className="font-bold text-emerald-600">{formatCurrency(detailHesap.giris_fatura_kesilen)}</p>
                      <span className="text-xs text-gray-400">Stok girişlerinden</span>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg shadow-sm border-l-4 border-purple-500">
                      <Label className="text-gray-500 text-xs">Alınan Fatura</Label>
                      <p className="font-bold text-purple-600">{formatCurrency(detailHesap.cikis_fatura_alinan)}</p>
                      <span className="text-xs text-gray-400">Stok çıkışlarından</span>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded-lg shadow-sm border-l-4 border-blue-500">
                      <Label className="text-blue-700 text-xs font-medium">Kesilmesi Gereken</Label>
                      <p className="font-bold text-blue-600">
                        {formatCurrency(detailHesap.kesilmesi_gereken_fatura || 0)}
                      </p>
                      <span className="text-xs text-blue-500">Alacak takiplerinden</span>
                    </div>
                    <div className="text-center p-2 bg-orange-50 rounded-lg shadow-sm border-l-4 border-orange-500">
                      <Label className="text-orange-700 text-xs font-medium">Alınması Gereken</Label>
                      <p className="font-bold text-orange-600">
                        {formatCurrency(detailHesap.alinmasi_gereken_fatura || 0)}
                      </p>
                      <span className="text-xs text-orange-500">Borç takiplerinden</span>
                    </div>
                  </div>
                </div>

                {/* Borç/Alacak Bilgileri */}
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <h4 className="text-sm font-medium text-purple-700 mb-2">Borç Durumu</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-2 bg-white rounded-lg shadow-sm border-l-4 border-cyan-500">
                      <Label className="text-gray-500 text-xs">Alınan Borç</Label>
                      <p className="font-bold text-cyan-600">{formatCurrency(detailHesap.giris_borc_alinan)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg shadow-sm border-l-4 border-pink-500">
                      <Label className="text-gray-500 text-xs">Verilen Borç</Label>
                      <p className="font-bold text-pink-600">{formatCurrency(detailHesap.cikis_borc_verilen)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Kapat
            </Button>
            <Button 
              onClick={() => {
                setDetailModalOpen(false);
                openEditModal(detailHesap);
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Edit className="h-4 w-4 mr-2" />
              Düzenle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Çek Detay Modalı */}
      <Dialog open={showCekModal} onOpenChange={setShowCekModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className={`h-5 w-5 ${selectedCekType === 'alinan' ? 'text-emerald-600' : 'text-orange-600'}`} />
              {selectedCariForCek?.ad} - {selectedCekType === 'alinan' ? 'Alınan Çekler' : 'Verilen Çekler'}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            {selectedCariForCek && getCeklerForCari(selectedCariForCek.ad, selectedCekType).length > 0 ? (
              <div className="space-y-3">
                {getCeklerForCari(selectedCariForCek.ad, selectedCekType).map((cek, idx) => (
                  <Card key={cek.id || idx} className={`border ${selectedCekType === 'alinan' ? 'border-emerald-200 bg-emerald-50/30' : 'border-orange-200 bg-orange-50/30'}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={selectedCekType === 'alinan' ? 'border-emerald-500 text-emerald-700' : 'border-orange-500 text-orange-700'}>
                            {cek.seri_no || `ÇK-${idx + 1}`}
                          </Badge>
                          <Badge variant={cek.durum === 'odenmis' ? 'default' : 'secondary'} className={cek.durum === 'odenmis' ? 'bg-green-500' : ''}>
                            {cek.durum === 'odenmis' ? 'Ödendi' : 'Ödenecek'}
                          </Badge>
                        </div>
                        <span className={`text-lg font-bold ${selectedCekType === 'alinan' ? 'text-emerald-700' : 'text-orange-700'}`}>
                          {formatCurrency(cek.tutar)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Çek No:</span>
                          <span className="ml-2 font-medium">{cek.cek_no || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Banka:</span>
                          <span className="ml-2 font-medium">{cek.banka || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Düzenleme:</span>
                          <span className="ml-2 font-medium">
                            {cek.duzenleme_tarihi ? new Date(cek.duzenleme_tarihi).toLocaleDateString('tr-TR') : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Vade:</span>
                          <span className="ml-2 font-medium">
                            {cek.vade_tarihi ? new Date(cek.vade_tarihi).toLocaleDateString('tr-TR') : '-'}
                          </span>
                        </div>
                        {cek.kesideci && (
                          <div>
                            <span className="text-gray-500">Kesideci:</span>
                            <span className="ml-2 font-medium">{cek.kesideci}</span>
                          </div>
                        )}
                        {cek.alici && (
                          <div>
                            <span className="text-gray-500">Alıcı:</span>
                            <span className="ml-2 font-medium">{cek.alici}</span>
                          </div>
                        )}
                      </div>
                      
                      {cek.aciklama && (
                        <div className="mt-3 pt-3 border-t text-sm">
                          <span className="text-gray-500">Açıklama:</span>
                          <p className="mt-1 text-gray-700">{cek.aciklama}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Bu cari hesaba ait {selectedCekType === 'alinan' ? 'alınan' : 'verilen'} çek bulunmuyor.</p>
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCekModal(false)}>
              <X className="h-4 w-4 mr-2" />
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingCariHesaplar;
